import { TypedDocumentNode, gql } from '@apollo/client';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { getScoreSimpleRewards } from '@sorare/core/src/lib/rewards';

import { ClickableRewardOverview } from 'components/rewards/ClickableRewardOverview';
import { Variant } from 'components/rewards/RewardOverview/types';
import { isFixtureOpened } from 'lib/so5';

import { RewardRange, RewardRangeVariant } from '../RewardRange';
import {
  ScoreRewards_so5League,
  ScoreRewards_so5Ranking,
  ScoreRewards_so5RewardConfig,
} from './__generated__/index.graphql';

const Container = styled.div`
  &.highlightSelected {
    opacity: 0.5;
    &.selected {
      opacity: 1;
    }
  }
`;

type Props = {
  so5RewardConfigs: ScoreRewards_so5RewardConfig[];
  defaultRarity: Rarity | null;
  so5League: ScoreRewards_so5League;
  so5Ranking?: ScoreRewards_so5Ranking;
  targetable?: boolean;
  hideCompletionStatus?: boolean;
};

export const ScoreRewards = ({
  so5RewardConfigs,
  defaultRarity,
  so5League,
  so5Ranking,
  targetable,
  hideCompletionStatus,
}: Props) => {
  const { exchangeRate } = useConfigContext();

  const isUpcoming = isFixtureOpened(so5League.so5Fixture);

  const scoreRewards = getScoreSimpleRewards({
    so5RewardConfigs,
    rarity: defaultRarity,
    rates: exchangeRate.ethRates,
  })
    .filter(scoreReward => scoreReward.score)
    .filter(scoreReward =>
      targetable ? !!scoreReward.target : !scoreReward.target
    )
    .sort((a, b) => (b.score || 0) - (a.score || 0));

  const myScore = so5Ranking?.score;
  const myEligibleRewardScore = so5Ranking?.eligibleRewards?.reduce(
    (highest, reward) => {
      if (typeof reward.score === 'number' && reward.score > highest) {
        return reward.score;
      }
      return highest;
    },
    0
  );

  if (!scoreRewards.length) {
    return null;
  }
  return (
    <Vertical gap={2}>
      {scoreRewards.map(scoreReward => {
        const selectedTarget = targetable
          ? so5Ranking?.targetedRewardWithEligibility?.reward.target?.slug ===
            scoreReward.target?.slug
          : true;

        const isMyRange =
          typeof myScore === 'number' &&
          myEligibleRewardScore === scoreReward.score &&
          myScore > scoreReward.score;

        return (
          <Container
            key={scoreReward.score}
            className={classNames({
              highlightSelected: !!so5Ranking,
              selected: !targetable || (targetable && selectedTarget),
            })}
          >
            <RewardRange
              variant={
                isUpcoming ? RewardRangeVariant.ROW : RewardRangeVariant.BLOCK
              }
              title={
                <Horizontal>
                  {targetable && (
                    <img
                      src={scoreReward.target?.pictureUrl}
                      alt=""
                      width={16}
                      height={16}
                    />
                  )}
                  {!targetable && !isUpcoming && !hideCompletionStatus && (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      color={
                        isMyRange
                          ? 'var(--c-rivals-win)'
                          : 'var(--c-neutral-600)'
                      }
                    />
                  )}
                  <BodyS as="p">
                    <FormattedMessage {...fantasy.score} /> {'≥'}{' '}
                    {scoreReward.score}
                  </BodyS>
                </Horizontal>
              }
              rewards={scoreReward.simpleRewards.map((simpleReward, i) => {
                return (
                  <ClickableRewardOverview
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    variant={Variant.CLASSIC}
                    {...simpleReward}
                    deliverableItems={scoreReward.deliverableItems
                      ?.map(item => item.deliverableItem)
                      .filter(Boolean)}
                    probabilisticBundleConfigs={
                      scoreReward.probabilisticBundleConfigs || []
                    }
                    experiences={scoreReward.experiences || []}
                    so5League={so5League}
                  />
                );
              })}
            />
          </Container>
        );
      })}
    </Vertical>
  );
};

ScoreRewards.fragments = {
  so5RewardConfig: gql`
    fragment ScoreRewards_so5RewardConfig on So5RewardConfig {
      score
      target {
        slug
        pictureUrl
      }
      deliverableItems {
        deliverableItem {
          slug
          ...ClickableRewardOverview_deliverableItem
        }
      }
      probabilisticBundleConfigs {
        id
        ...ClickableRewardOverview_probabilisticBundleConfig
      }
      experiences {
        ...ClickableRewardOverview_so5RewardExperience
      }
      ...getScoreSimpleRewards_so5RewardConfig
    }
    ${ClickableRewardOverview.fragments.deliverableItem}
    ${ClickableRewardOverview.fragments.probabilisticBundleConfig}
    ${ClickableRewardOverview.fragments.so5RewardExperience}
    ${getScoreSimpleRewards.fragments.so5RewardConfig}
  ` as TypedDocumentNode<ScoreRewards_so5RewardConfig>,
  so5League: gql`
    fragment ScoreRewards_so5League on So5League {
      slug
      so5Fixture {
        slug
        aasmState
      }
      ...ClickableRewardOverview_so5League
    }
    ${ClickableRewardOverview.fragments.so5League}
  ` as TypedDocumentNode<ScoreRewards_so5League>,
  so5Ranking: gql`
    fragment ScoreRewards_so5Ranking on So5Ranking {
      id
      score
      so5Lineup {
        id
        user {
          slug
          nickname
        }
      }
      eligibleRewards {
        score
      }
      targetedRewardWithEligibility {
        reward {
          target {
            slug
          }
        }
      }
    }
  ` as TypedDocumentNode<ScoreRewards_so5Ranking>,
};
